interface AclRole {
  id: number;
  alias: string;
}

interface PageResources {
  resources: Set<string>;
  loading: boolean;
}

interface ComponentResources {
  resources: Set<string>;
  loading: boolean;
}

interface CurrentUser {
  id: number;
  name: string;
  email: string;
  role: string;
}

export default function useRoles() {
  const pageResources = useState<PageResources>('page-resources', () => ({
    resources: new Set<string>(),
    loading: true,
  }));

  const componentResources = useState<ComponentResources>('component-resources', () => ({
    resources: new Set<string>(),
    loading: true,
  }));

  const currentUser = useState<CurrentUser | null>('current-user', () => null);

  /**
   * Получает токен из localStorage
   */
  const getToken = (): string | null => localStorage.getItem('token');

  /**
   * Получает роли пользователя из access токена
   */
  const getUserRoles = (): AclRole[] => {
    const token = getToken();

    if (!token) {
      return [];
    }

    try {
      return getRolesFromToken(token);
    } catch {
      return [];
    }
  };

  /**
   * Получает ID пользователя из токена
   */
  const getUserId = (): number | null => {
    const token = getToken();

    if (!token) {
      return null;
    }

    return getUserIdFromToken(token);
  };

  /**
   * Загружает информацию о текущем пользователе
   */
  const loadUserInfo = async (): Promise<void> => {
    try {
      const userId = getUserId();

      if (!userId) {
        currentUser.value = null;

        return;
      }

      const { admin } = useRequest();
      const { payload } = await admin.adminControllerGetEntityByPk(String(userId));

      // Получаем роли пользователя
      const roles = getUserRoles();
      const isSuperAdminUser = roles.some(role => role.alias === 'kernel_super_admin');

      // Формируем строку роли
      let roleString: string;

      if (isSuperAdminUser) {
        roleString = 'Super Admin';
      } else if (payload.aclRoles && payload.aclRoles.length > 0) {
        // Получаем названия ролей из aclRoles через запятую
        roleString = payload.aclRoles.map(role => role.title).join(', ');
      } else {
        roleString = roles.map(role => role.alias).join(', ');
      }

      // Формируем полное имя
      const name = `${payload.firstname} ${payload.lastname}`.trim();

      currentUser.value = {
        id: payload.id,
        name,
        email: payload.email,
        role: roleString,
      };
    } catch {
      currentUser.value = null;
    }
  };

  /**
   * Загружает все ресурсы (страниц и компонентов) для ролей пользователя, также загружает информацию о пользователе
   */
  const loadPageResources = async (): Promise<void> => {
    pageResources.value.loading = true;
    componentResources.value.loading = true;

    try {
      // Загружаем информацию о пользователе
      await loadUserInfo();

      const roles = getUserRoles();

      if (roles.length === 0) {
        pageResources.value.resources = new Set();
        componentResources.value.resources = new Set();

        return;
      }

      // Проверяем, является ли пользователь суперадмином
      const isSuperAdmin = roles.some(role => role.alias === 'kernel_super_admin');

      if (isSuperAdmin) {
        // Суперадмин имеет доступ ко всем страницам и компонентам
        // Используем специальный маркер для суперадмина
        pageResources.value.resources = new Set(['*']);
        componentResources.value.resources = new Set(['*']);

        return;
      }

      // Для обычных ролей получаем ресурсы через API
      const allPageResources = new Set<string>();
      const allComponentResources = new Set<string>();

      // Получаем ресурсы для каждой роли
      await Promise.all(
        roles.map(async (role) => {
          try {
            // Запрашиваем все ресурсы nrm3
            const resources = await getAllNrm3RoleResources(role.id, 'nrm3.');

            resources.forEach((resource) => {
              const resourceName = resource.resource;

              // Распределяем ресурсы по типам
              if (resourceName.startsWith('nrm3.pages.')) {
                allPageResources.add(resourceName);
              } else if (resourceName.startsWith('nrm3.components.')) {
                allComponentResources.add(resourceName);
              }
            });
          } catch {
            // Игнорируем ошибки для отдельных ролей
          }
        }),
      );

      pageResources.value.resources = allPageResources;
      componentResources.value.resources = allComponentResources;
    } catch {
      pageResources.value.resources = new Set();
      componentResources.value.resources = new Set();
    } finally {
      pageResources.value.loading = false;
      componentResources.value.loading = false;
    }
  };

  /**
   * Проверяет, есть ли у пользователя доступ к странице
   * @param pageName - Имя страницы (route name)
   * @returns true, если доступ есть
   */
  const hasPageAccess = (pageName: string): boolean => {
    // Страница index всегда доступна
    if (pageName === 'index') {
      return true;
    }

    // Суперадмин имеет доступ ко всем страницам
    if (pageResources.value.resources.has('*')) {
      return true;
    }

    const resource = `nrm3.pages.${pageName}`;

    // Проверяем точное совпадение
    if (pageResources.value.resources.has(resource)) {
      return true;
    }

    // Проверяем родительские пути
    // Например, если есть доступ к 'document-registry',
    // то разрешаем доступ к 'document-registry-10409'
    // Разбиваем по дефисам, так как имена маршрутов используют дефисы
    const pathParts = pageName.split('-');

    // Проверяем каждый родительский путь, начиная с самого длинного
    for (let i = pathParts.length - 1; i > 0; i--) {
      const parentPath = pathParts.slice(0, i).join('-');

      // Пропускаем проверку для index, так как это главная страница
      // и доступ к ней не должен давать доступ ко всем подпутям
      if (parentPath === 'index') {
        continue;
      }

      const parentResource = `nrm3.pages.${parentPath}`;

      if (pageResources.value.resources.has(parentResource)) {
        return true;
      }
    }

    return false;
  };

  /**
   * Проверяет, есть ли у пользователя доступ к ресурсу
   * @param resourceName - Полное имя ресурса (например, 'nrm3.pages.network-entire-network')
   * @returns true, если доступ есть
   */
  const hasResourceAccess = (resourceName: string): boolean =>
    pageResources.value.resources.has('*') || pageResources.value.resources.has(resourceName);

  /**
   * Получает список всех доступных ресурсов страниц
   */
  const getPageResources = (): string[] =>
    Array.from(pageResources.value.resources);

  /**
   * Проверяет, есть ли у пользователя доступ к компоненту
   * @param componentName - Имя компонента
   * @returns true, если доступ есть
   */
  const hasComponentAccess = (componentName: string): boolean => {
    // Суперадмин имеет доступ ко всем компонентам
    if (componentResources.value.resources.has('*')) {
      return true;
    }

    const resource = `nrm3.components.${componentName}`;

    return componentResources.value.resources.has(resource);
  };

  /**
   * Получает список всех доступных ресурсов компонентов
   */
  const getComponentResources = (): string[] =>
    Array.from(componentResources.value.resources);

  /**
   * Проверяет, является ли пользователь суперадмином
   */
  const isSuperAdmin = (): boolean => {
    const roles = getUserRoles();

    return roles.some(role => role.alias === 'kernel_super_admin');
  };

  return {
    // Состояние
    loading: computed(() => pageResources.value.loading),
    resources: computed(() => pageResources.value.resources),
    componentResources: computed(() => componentResources.value.resources),
    componentLoading: computed(() => componentResources.value.loading),
    currentUser: computed(() => currentUser.value),

    // Методы
    loadPageResources,
    loadUserInfo,
    hasPageAccess,
    hasResourceAccess,
    hasComponentAccess,
    getPageResources,
    getComponentResources,
    getUserRoles,
    isSuperAdmin,
  };
}

